import React, { useEffect, useState } from 'react';
import {
  getSnackBar,
  useNotification,
  showBackdropLoader,
} from 'utils/commonUtils';
import {
  ColumnStatusContainer,
  TrackerColumnFormControl,
} from '../../ssaComponentStyled';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import MDTypography from 'components/MDTypography';
import { useDispatch, useSelector } from 'react-redux';
import { AccordianContainer } from 'components/Ui/styled';
import {
  getTrackers,
  putHideUnHideColumn,
} from 'layouts/studentTracking/services/studentTrackerApi';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { AccordionLabel } from './SsaStudentTrackersTabUtils';
import { SsaStudentProfileLoader } from '../../ssaCommonUtils';
import CustomizedSnackbars from 'components/snackbar/Snackbar';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import { FormInputSwitch } from 'components/Common/Forms/FormInputSwitch';
import { setSelectedStudentTrackers } from 'layouts/studentSuccessAdvocate/ssa/store/ssaStudentTrackersSlice/ssaStudentTrackersSlice';

const SsaStudentTrackersTab = ({ currentSemester }) => {
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const dispatch = useDispatch();

  const selectedTrackers = useSelector(
    (state) => state?.ssaStudentTrackers?.selectedStudentTrackers || [],
  );
  const ssaStudentDetail = useSelector(
    (state) => state?.ssaStudentDetail?.selectedStudentHeadingDetails || {},
  );
  const { grade } = ssaStudentDetail || {};

  const [trackers, setTrackers] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState([]);
  const [checkedColumns, setCheckedColumns] = useState({});
  const [isCheckColumnLoading, setIsCheckColumnLoading] = useState(false);

  const { control, watch, reset } = useForm({
    defaultValues: {
      showCurrentSemesterOnly: true,
    },
  });

  const showCurrentSemesterOnlywatch = watch('showCurrentSemesterOnly');

  useEffect(() => {
    reset({
      showCurrentSemesterOnly: true,
    });
  }, [reset]);

  useEffect(() => {
    if (!selectedTrackers?.length) return;

    const initialChecked = {};

    selectedTrackers?.forEach((tracker) => {
      initialChecked[tracker?.trackerId] = tracker?.columns || [];
    });

    setCheckedColumns(initialChecked);
  }, [selectedTrackers]);

  useEffect(() => {
    fetchTrackers();
  }, []);

  const fetchTrackers = async () => {
    try {
      setTabLoading(true);
      const data = await getTrackers();
      setTrackers(data);

      // Initialize all panels as expanded when data is loaded
      if (data?.trackers) {
        const allPanelIds = data?.trackers?.map((_, index) => `panel-${index}`);
        setExpandedPanels(allPanelIds);
      }

      setTabLoading(false);
    } catch (error) {
      setTabLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  // const handleToggleColumn = async (tracker, column) => {
  //   const snackBar = getSnackBar('Column updated successfully');

  //   const payload = { columnName: column };

  //   try {
  //     setIsCheckColumnLoading(true);
  //     await putHideUnHideColumn(payload, tracker?.trackerId, grade);
  //     dispatch(displaySnackbar(snackBar));

  //     const updatedData = await getTrackers();
  //     setIsCheckColumnLoading(false);
  //     setTrackers(updatedData);
  //   } catch (error) {
  //     setIsCheckColumnLoading(false);
  //     handleErrorResponse(error?.response?.data?.message);
  //   }
  // };

  const handleToggleColumn = (tracker, column) => {
    setCheckedColumns((prev) => {
      const existing = prev[tracker?.trackerId] || [];

      let updatedColumns;

      if (existing?.includes(column)) {
        updatedColumns = existing?.filter((c) => c !== column);
      } else {
        updatedColumns = [...existing, column];
      }

      const newState = {
        ...prev,
        [tracker?.trackerId]: updatedColumns,
      };

      const selectedTrackers = Object.keys(newState)
        .filter((trackerId) => newState[trackerId]?.length > 0)
        .map((trackerId) => {
          const trackerInfo = trackers?.trackers?.find(
            (t) => t?.trackerId === trackerId,
          );

          return {
            name: trackerInfo?.name,
            columns: newState[trackerId], // only selected columns
            version: trackerInfo?.version,
            semester: trackerInfo?.semester,
            trackerId: trackerInfo?.trackerId,
            statusOptions: trackerInfo?.statusOptions,
          };
        });

      dispatch(setSelectedStudentTrackers(selectedTrackers));

      return newState;
    });
  };

  // const isColumnChecked = (tracker, col) => {
  //   return tracker?.hiddenColumnsByGrade?.[grade]?.includes(col) || false;
  // };

  const isColumnChecked = (trackerId, column) => {
    return checkedColumns?.[trackerId]?.includes(column) || false;
  };

  const handleChange = (panel) => (event, isExpanded) => {
    if (isExpanded) {
      setExpandedPanels((prev) => [...prev, panel]);
    } else {
      setExpandedPanels((prev) => prev?.filter((p) => p !== panel));
    }
  };

  const isPanelExpanded = (panel) => {
    return expandedPanels?.includes(panel);
  };

  if (tabLoading) {
    return <SsaStudentProfileLoader />;
  }

  const filteredTrackers = trackers?.trackers?.filter((tracker) => {
    if (!showCurrentSemesterOnlywatch) return true;
    return tracker?.semester === currentSemester;
  });

  const selectedColumnCount = Object.values(checkedColumns)?.flat()?.length;

  return (
    <>
      <CustomizedSnackbars />
      {isCheckColumnLoading && showBackdropLoader(true)}

      <Typography variant="h6" sx={{ mb: 2 }}>
        Select which tracker columns to display on the Attendance tab
      </Typography>

      <Grid container>
        <Grid item xs={2.5}>
          <FormInputSwitch
            control={control}
            name="showCurrentSemesterOnly"
            label="Show current semester only"
          />
        </Grid>
      </Grid>

      <Grid container sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <ColumnStatusContainer>
            <MDTypography color="text" variant="button" fontWeight="regular">
              {selectedColumnCount > 0
                ? `${selectedColumnCount} column currently enabled`
                : 'No columns currently enabled'}
            </MDTypography>
          </ColumnStatusContainer>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {filteredTrackers?.map((tracker, index) => {
          const panelName = `panel-${index}`;
          return (
            <Grid item xs={12} key={tracker.trackerId}>
              <AccordianContainer
                expanded={isPanelExpanded(panelName)}
                onChange={handleChange(panelName)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <AccordionLabel tracker={tracker} />
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={3}>
                    {tracker?.columns.map((column, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        key={`${tracker.trackerId}-${column}-${index}`}
                      >
                        <TrackerColumnFormControl
                          control={
                            // <Checkbox
                            //   checked={isColumnChecked(tracker, column)}
                            //   onChange={() =>
                            //     handleToggleColumn(tracker, column)
                            //   }
                            // />
                            <Checkbox
                              checked={isColumnChecked(
                                tracker.trackerId,
                                column,
                              )}
                              onChange={() =>
                                handleToggleColumn(tracker, column)
                              }
                            />
                          }
                          label={column}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </AccordianContainer>
            </Grid>
          );
        })}
      </Grid>
      <NotificationPopup />
    </>
  );
};

export default SsaStudentTrackersTab;
